import { useState } from 'react';
import { useList, useTranslate } from '@refinedev/core';
import {
  Button,
  Card,
  Empty,
  Flex,
  Tag,
  Timeline,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import type { CycleDeltaLogOut } from '@/api/generated/schemas';
import { SecondsRange } from '@/components/common/SecondsRange';
import {
  formatDateTime,
  formatFraction,
  formatSecondsClock,
  formatSecondsVerbose,
} from '@/utils/format';

const PAGE = 10;

/** Layer writes behind a cycle's coverage, newest first. */
export const CycleDeltaLogCard = ({ cycleId }: { cycleId: number }) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const [limit, setLimit] = useState(PAGE);

  const { result, query } = useList<CycleDeltaLogOut>({
    resource: 'settlement.cycle_delta_logs',
    filters: [{ field: 'cycle_id', operator: 'eq', value: cycleId }],
    // recorded_at is the billing clock and the partition column.
    sorters: [{ field: 'recorded_at', order: 'desc' }],
    pagination: { pageSize: limit, mode: 'server' },
  });

  const entries = result?.data ?? [];

  return (
    <Card
      title={t('settlement.cycles.sections.delta_log')}
      loading={query.isLoading}
    >
      {entries.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Flex vertical gap={token.margin}>
          <Timeline
            items={entries.map((entry) => ({
              key: entry.id,
              color: entry.min_crossed ? 'green' : 'blue',
              children: (
                <Flex vertical gap={token.marginXXS}>
                  <Typography.Text type="secondary">
                    {formatDateTime(entry.recorded_at)}
                  </Typography.Text>

                  <Typography.Text strong>
                    {`+${formatSecondsVerbose(entry.delta_seconds)}`}
                  </Typography.Text>

                  <Typography.Text type="secondary">
                    {`${formatSecondsClock(entry.covered_seconds_before)} → ${formatSecondsClock(entry.covered_seconds_after)} · `}
                    <Tooltip title={entry.covered_fraction_after}>
                      <span>
                        {formatFraction(entry.covered_fraction_after)}
                      </span>
                    </Tooltip>
                  </Typography.Text>

                  <Typography.Text type="secondary">
                    {t('settlement.cycles.delta_log.segments_count', {
                      count: entry.segments_count,
                    })}
                  </Typography.Text>

                  {entry.added_ranges.map((range) => (
                    <SecondsRange
                      key={`${range.lower}-${range.upper}`}
                      value={range}
                    />
                  ))}

                  {entry.min_crossed && (
                    <Tag color="green" style={{ alignSelf: 'flex-start' }}>
                      {t('settlement.cycles.delta_log.min_crossed')}
                    </Tag>
                  )}
                </Flex>
              ),
            }))}
          />
          {(result?.total ?? 0) > limit && (
            <Button
              block
              loading={query.isFetching}
              onClick={() => setLimit((prev) => prev + PAGE)}
            >
              {t('settlement.cycles.delta_log.load_more')}
            </Button>
          )}
        </Flex>
      )}
    </Card>
  );
};
